import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { fetchSiteContent } from "@/services/api";
import HomePage from "@/pages/HomePage";
import SolarPage from "@/pages/SolarPage";
import RoofingPage from "@/pages/RoofingPage";
import AboutPage from "@/pages/AboutPage";
import GalleryPage from "@/pages/GalleryPage";
import ContactPage from "@/pages/ContactPage";
import SolarBusinessPage from "@/pages/SolarBusinessPage";

function App() {
  const [siteData, setSiteData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSiteData = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const content = await fetchSiteContent();
      setSiteData(content);
    } catch (requestError) {
      setError("We couldn't load the site details right now. Please try again.");
      console.error("Failed to load site content", requestError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSiteData();
  }, [loadSiteData]);

  return (
    <HelmetProvider>
      <ThemeProvider attribute="class" forcedTheme="light" enableSystem={false}>
        <BrowserRouter>
          <Routes>
            <Route
              element={
                <SiteLayout
                  siteData={siteData}
                  isLoading={isLoading}
                  error={error}
                  onRetry={loadSiteData}
                />
              }
            >
              <Route index element={<HomePage />} />
              <Route path="/solar-services" element={<SolarPage />} />
              <Route path="/roofing-services" element={<RoofingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/solar-installation-california" element={<SolarBusinessPage buttonLabel="Request installation quote" formId="installation-quote-form" pageKey="installation" pageSource="solar-installation-california" relatedLinks={[{ label: "Solar repair", to: "/solar-repair-california" }, { label: "Battery storage", to: "/battery-storage-california" }, { label: "California service areas", to: "/california-service-areas" }]} />} />
              <Route path="/solar-repair-california" element={<SolarBusinessPage buttonLabel="Request repair quote" formId="repair-quote-form" pageKey="repair" pageSource="solar-repair-california" relatedLinks={[{ label: "Solar installation", to: "/solar-installation-california" }, { label: "Battery storage", to: "/battery-storage-california" }, { label: "Service areas", to: "/california-service-areas" }]} />} />
              <Route path="/battery-storage-california" element={<SolarBusinessPage buttonLabel="Request storage quote" formId="storage-quote-form" pageKey="storage" pageSource="battery-storage-california" relatedLinks={[{ label: "Solar installation", to: "/solar-installation-california" }, { label: "Solar repair", to: "/solar-repair-california" }, { label: "Service areas", to: "/california-service-areas" }]} />} />
              <Route path="/california-service-areas" element={<SolarBusinessPage buttonLabel="Request local quote" formId="areas-quote-form" pageKey="areas" pageSource="california-service-areas" relatedLinks={[{ label: "Solar installation", to: "/solar-installation-california" }, { label: "Roofing services", to: "/roofing-services" }, { label: "Contact Monarch", to: "/contact" }]} />} />
              <Route path="*" element={<Navigate replace to="/" />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
